// Pre-routes (spec §3.12, §3.6, §5): routing known before the A2000 pick ticket exists. The order
// # is A2000 CTRL_NO (7 digits; ORDER_NO is the customer PO), checked against the dataset
// `routing-orders` (VR_ORDER_HD: open orders and those closed in the last 30 days). Never filtered
// or checked on warehouse: most open orders have none (it is decided per pick ticket).
//
// - Waiting until a pick ticket of the order is in the WMS; then its entries go onto every open
//   Worklist line holding the order's pick tickets (an order can split into several WMS lines) and
//   it is Moved. Only blanks are filled: a value a person entered stays, and the difference is said.
// - Closed in A2000 with no pick ticket: Cancelled (CNCL, B-CNCL) or Shipped (SHIP). An order no
//   longer in `routing-orders` keeps its last known status — nothing is guessed.
// - Alerts: Waiting, and its pickup 2 business days away or less (weekends and holidays skipped),
//   or already past.
//
// New pre-routes are A2000's only (`PRE_ROUTE_ADD_SOURCES`): every open Deposco SO is a Worklist line
// from the moment it exists (`LINES_WAIT_FOR_ALLOCATION` off, Isaac 10/30). A Deposco pre-route added
// before (typed with the SO #, SO230792, checked against `routing-deposco-orders`) still moves: the SO
// takes the order's place (`deposcoOrder`) and is its own "pick ticket" while open (`deposcoOrderPts`);
// it moves once its line is on the Worklist (an SO with a DC waits for its group); Canceled →
// Cancelled, Ship Complete → Shipped.
//
// Pure: what to write is decided here; who writes it is `frontend/.../pre-route-mover.ts` today
// (in the session of whoever has a routing page open), the server once it can (P17).

import type { PreRouteSource, PreRouteStatus } from "../entities";
import type { LineView } from "./assemble";
import { editSideEffects } from "./assemble";
import { isBlankEntry } from "./carry";
import type { FieldChange } from "./change-log";
import { fieldChange } from "./change-log";
import type { Holidays } from "./days";
import type { DeposcoRow } from "./deposco";
import {
  DEPOSCO_CORP,
  DEPOSCO_FACILITY_WH,
  DEPOSCO_SHIPPED,
  deposcoDivisionName,
  DEPOSCO_WAITS_FOR,
  LINES_WAIT_FOR_ALLOCATION,
  deposcoLineKey,
  isCanceledStatus,
  isLineSo,
  isSoNo,
  soNoOf,
} from "./deposco";
import type { DivisionNamer } from "./divisions";
import { divisionNamer, divisionWords } from "./divisions";
import type { CustomerFace, CustomerNamer } from "./customer-names";
import { customerNamer } from "./customer-names";
import { businessDaysBetween, dayOfWeek, formatDay, formatShortDay } from "./days";
import { readBaseline } from "./integrity";
import type {
  CustomerNameRecord,
  DivisionAbbrevRecord,
  LinePickTicketRecord,
  RoutingLineRecord,
  SourceRow,
  WarehouseField,
} from "./types";

/**
 * One A2000 order, as `dbo.vw_routing_orders` gives it (dataset `routing-orders`) — or a Deposco SO
 * in its place (`deposcoOrder`: `ctrl_no` its SO #). Days are YYYY-MM-DD.
 */
export interface OrderRow {
  ctrl_no: string;
  customer_po: string | null;
  customer_code: string | null;
  customer_name: string | null;
  corp: string | null;
  div: string | null;
  division: string | null;
  store: string | null;
  wh: string | null;
  dc_no: string | null;
  dc_name: string | null;
  start_date: string | null;
  cancel_date: string | null;
  qty: number | null;
  /** OPEN, PART (open); SHIP, CNCL, B-CNCL (closed). */
  status: string | null;
  is_open: 0 | 1;
  order_hold: string | null;
  cancel_code: string | null;
  canceled_on: string | null;
  ship_via: string | null;
  order_date: string | null;
  entry_date: string | null;
  modified_on: string | null;
  source_loaded_at: string | null;
}

/** A `pre-route` record as the store lists it. */
export interface PreRouteRecord {
  id: string;
  source: PreRouteSource;
  orderNo: string;
  routingRef: string;
  appointmentDate: string | null;
  notes: string;
  status: PreRouteStatus;
  /** ISO: when a session claimed the move; "" while Waiting. */
  movedAt: string;
  /** The lines it moved onto, "RL-12,RL-13"; "" until the move is done. */
  movedLineIds: string;
  moveDone: "No" | "Yes" | null;
  addedBy: string;
  orderStatus: string;
  canceledOn: string | null;
  cancelCode: string;
  customerName: string;
  customerPo: string;
  division: string;
  qty: number | null;
  startDate: string | null;
  cancelDate: string | null;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * An order's pick tickets in the open source: all of them, those in the WMS, their WMS #s and
 * warehouses, and their QTY together (null when none has one).
 */
export interface OrderPts {
  pickTickets: string[];
  inWms: string[];
  wms: string[];
  whs: string[];
  qty: number | null;
  /** A Deposco SO's line (its key), once it is one; [] for A2000. */
  groups: string[];
}

export const NO_PTS: OrderPts = { pickTickets: [], inWms: [], wms: [], whs: [], qty: null, groups: [] };

/** A Deposco SO in an order's place: what the pre-route rules read. */
export function deposcoOrder(r: DeposcoRow): OrderRow {
  const facility = String(r.facility ?? "").trim();
  const division = String(r.division_code ?? "").trim();
  return {
    ctrl_no: r.so_number,
    customer_po: r.customer_po,
    customer_code: r.customer_code,
    customer_name: r.customer_name,
    corp: DEPOSCO_CORP,
    div: division || null,
    division: division ? deposcoDivisionName(division) : null,
    store: r.ship_to_name,
    wh: DEPOSCO_FACILITY_WH[facility] ?? (facility || null),
    dc_no: r.dc_no,
    dc_name: null,
    start_date: r.start_date,
    cancel_date: r.cancel_date,
    qty: r.qty,
    status: r.deposco_status,
    is_open: r.is_open,
    order_hold: null,
    cancel_code: null,
    canceled_on: null,
    ship_via: r.ship_type,
    order_date: r.received_date,
    entry_date: r.received_date,
    modified_on: r.deposco_updated ? r.deposco_updated.slice(0, 10) : null,
    source_loaded_at: r.source_loaded_at,
  };
}

/**
 * A Deposco SO as its own "pick ticket": one while it is an SO of the lines (`isLineSo`: open — and
 * allocated, while `LINES_WAIT_FOR_ALLOCATION` is on); "in the WMS" (ready to move) once its line
 * exists too — an SO with a DC waits for its group.
 */
export function deposcoOrderPts(r: DeposcoRow): OrderPts {
  const key = deposcoLineKey(r);
  const live = isLineSo(r);
  const wh = deposcoOrder(r).wh;
  return {
    pickTickets: live ? [r.so_number] : [],
    inWms: live && key ? [r.so_number] : [],
    wms: [],
    whs: wh ? [wh] : [],
    qty: live ? r.qty : null,
    groups: key ? [key] : [],
  };
}

/** The A2000 order # as typed: digits, no spaces. */
export const orderNoOf = (typed: string) => typed.replace(/\s+/g, "");

/** Each order's open pick tickets (by CTRL_NO), for the orders asked; an order with none is absent. */
export function orderPickTickets(source: readonly SourceRow[], orderNos: Iterable<string>): Record<string, OrderPts> {
  const wanted = new Set(orderNos);
  const out: Record<string, OrderPts> = {};
  for (const s of source) {
    const no = s.ctrl_no ?? "";
    if (!no || !wanted.has(no)) continue;
    const o = (out[no] ??= { pickTickets: [], inWms: [], wms: [], whs: [], qty: null, groups: [] });
    const pt = String(s.pick_ticket);
    o.pickTickets.push(pt);
    if (s.qty !== null) o.qty = (o.qty ?? 0) + s.qty;
    if (s.in_wms === 1 && s.wms_shipment) {
      o.inWms.push(pt);
      if (!o.wms.includes(s.wms_shipment)) o.wms.push(s.wms_shipment);
    }
    if (s.wh && !o.whs.includes(s.wh)) o.whs.push(s.wh);
  }
  return out;
}

/**
 * Managers only: each order's open pick tickets' $ value together (`routing-pick-ticket-values`);
 * an order none of whose pick tickets has a value is absent.
 */
export function orderPtValues(
  orderPts: Readonly<Record<string, OrderPts>>,
  values: Readonly<Record<string, number>>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [no, pts] of Object.entries(orderPts))
    for (const pt of pts.pickTickets) if (values[pt] !== undefined) out[no] = (out[no] ?? 0) + values[pt];
  return out;
}

/** A Moved pre-route's pick tickets as the app keeps them on its lines: how many, and their QTY together. */
export interface MovedCopies {
  pickTickets: number;
  qty: number | null;
}

/**
 * Each Moved pre-route's pick tickets as its moved lines keep them (a `line-pick-ticket` record,
 * else the line's baseline): they stay after A2000 ships them and drops them from the open view.
 * A copy has no CTRL_NO, so the order's are those it lists open now, or with its PO; a line never
 * approved has none (its pick tickets are still open). By pre-route id; one with none is absent.
 */
export function movedCopies(input: {
  preRoutes: readonly PreRouteRecord[];
  orders: Readonly<Record<string, OrderRow>>;
  orderPts: Readonly<Record<string, OrderPts>>;
  lines: readonly RoutingLineRecord[];
  pickTickets: readonly LinePickTicketRecord[];
}): Record<string, MovedCopies> {
  const moved = input.preRoutes.filter((p) => p.status === "Moved" && p.movedLineIds);
  const wanted = new Set(moved.flatMap((p) => p.movedLineIds.split(",").filter(Boolean)));
  // Per line, per pick ticket: a record wins over the baseline.
  const copiesOf = new Map<string, Map<string, LinePickTicketRecord>>();
  const hold = (c: LinePickTicketRecord) => {
    if (!wanted.has(c.lineId)) return;
    const m = copiesOf.get(c.lineId) ?? new Map<string, LinePickTicketRecord>();
    m.set(String(c.pickTicket), c);
    copiesOf.set(c.lineId, m);
  };
  for (const l of input.lines) if (wanted.has(l.id)) for (const c of readBaseline(l).copies) hold(c);
  for (const c of input.pickTickets) hold(c);
  const out: Record<string, MovedCopies> = {};
  for (const p of moved) {
    const open = new Set(input.orderPts[p.orderNo]?.pickTickets ?? []);
    const po = (input.orders[p.orderNo]?.customer_po ?? p.customerPo).trim();
    const mine = new Map<string, LinePickTicketRecord>();
    // A Deposco SO is its own copy: never the PO's other SOs on the line.
    const ours = (pt: string, c: LinePickTicketRecord) =>
      p.source === "Deposco"
        ? pt === p.orderNo
        : open.has(pt) || (!!po && (c.customerPo.trim() === po || c.orderNo.trim() === po));
    for (const id of p.movedLineIds.split(",").filter(Boolean))
      for (const [pt, c] of copiesOf.get(id) ?? []) if (ours(pt, c)) mine.set(pt, c);
    if (!mine.size) continue;
    const qtys = [...mine.values()].map((c) => c.qty).filter((q): q is number => q !== null);
    out[p.id] = { pickTickets: mine.size, qty: qtys.length ? qtys.reduce((a, b) => a + b, 0) : null };
  }
  return out;
}

const CLOSED: Readonly<Record<string, Exclude<PreRouteStatus, "Waiting" | "Moved">>> = {
  SHIP: "Shipped",
  CNCL: "Cancelled",
  "B-CNCL": "Cancelled",
};

/** How a closed order ends a waiting pre-route: A2000 CNCL / B-CNCL / SHIP, Deposco Canceled / Ship Complete. */
function closedAs(order: Pick<OrderRow, "status">, source: PreRouteSource) {
  if (source === "A2000") return CLOSED[String(order.status ?? "").toUpperCase()];
  if (isCanceledStatus(order.status)) return "Cancelled" as const;
  return String(order.status ?? "").toLowerCase() === DEPOSCO_SHIPPED.toLowerCase() ? ("Shipped" as const) : undefined;
}

/** What a pre-route of each source waits for, in words (the stage, the alert). */
const WAIT_WORDS: Record<
  PreRouteSource,
  Record<"no-pt" | "not-in-wms", { now: string; still: string; was: string }>
> = {
  A2000: {
    "no-pt": { now: "No pick ticket yet", still: "no pick ticket yet", was: "still no pick ticket" },
    "not-in-wms": {
      now: "Pick ticket, not in WMS yet",
      still: "pick ticket not in WMS yet",
      was: "pick ticket still not in WMS",
    },
  },
  Deposco: LINES_WAIT_FOR_ALLOCATION
    ? {
        "no-pt": { now: "Not allocated yet", still: "not allocated yet", was: "still not allocated" },
        "not-in-wms": {
          now: "Allocated, waiting for a group",
          still: "waiting for a group",
          was: "still waiting for a group",
        },
      }
    : {
        "no-pt": { now: "Not on the Worklist yet", still: "not on the Worklist yet", was: "still not on the Worklist" },
        "not-in-wms": {
          now: "Not on the Worklist yet — waiting for a group",
          still: "not on the Worklist yet (waiting for a group)",
          was: "still waiting for a group",
        },
      },
};

/** Where a Deposco SO stands for a pre-route: "on the Worklist", "not on the Worklist yet (waiting for a group)"… */
function deposcoStage(pts: OrderPts): string {
  if (LINES_WAIT_FOR_ALLOCATION) return pts.pickTickets.length ? "allocated" : "not allocated yet";
  if (pts.inWms.length) return "on the Worklist";
  return pts.pickTickets.length ? "not on the Worklist yet (waiting for a group)" : "not on the Worklist yet";
}

/**
 * The sources a new pre-route can be added for: A2000 only while every open Deposco SO is a line
 * (`LINES_WAIT_FOR_ALLOCATION` off) — pre-routing is for orders with no inventory yet, which for
 * A2000 (ERP and WMS) is "no pick ticket yet". Deposco pre-routes added before still move.
 */
export const PRE_ROUTE_ADD_SOURCES: readonly PreRouteSource[] = LINES_WAIT_FOR_ALLOCATION
  ? ["A2000", "Deposco"]
  : ["A2000"];

/** A2000's closed status in words: "cancelled (CNCL) on 10/02". */
function closedWords(order: Pick<OrderRow, "status" | "canceled_on">): string {
  const status = String(order.status ?? "").toUpperCase();
  const on = order.canceled_on ? ` on ${formatDay(order.canceled_on)}` : "";
  if (status === "SHIP") return "shipped (SHIP)";
  if (status === "CNCL" || status === "B-CNCL") return `cancelled (${status})${on}`;
  return `closed (${status || "no status"})`;
}

/** A part of the "Found in A2000" line; the division is its abbreviation, its full name the tip. */
export interface FoundPart {
  text: string;
  tip?: string;
}

/**
 * "Found in A2000 · Marshalls · PO 8574410 · SHOES · 288 units · start 10/22 · cancel 10/27 · no pick
 * ticket yet"; a Deposco SO (Figure 29): "Found in Deposco · Burlington · PO 716602609 · EGC · 528
 * units · start 10/10 · cancel 10/20 · WH PE · on the Worklist" (a pre-route from before).
 */
export function foundParts(
  order: OrderRow,
  pts: OrderPts,
  abbrev: DivisionNamer = divisionNamer(),
  source: PreRouteSource = "A2000",
): FoundPart[] {
  const division = order.division ?? "";
  const short = abbrev({ corp: order.corp ?? "", div: order.div ?? "", name: division });
  if (source === "Deposco")
    return [
      { text: "Found in Deposco" },
      { text: order.customer_name || order.customer_code || "—" },
      { text: order.customer_po ? `PO ${order.customer_po}` : "" },
      { text: short, ...(short !== division ? { tip: division } : {}) },
      { text: order.qty !== null ? `${order.qty.toLocaleString("en-US")} units` : "" },
      { text: order.start_date ? `start ${formatShortDay(order.start_date)}` : "" },
      { text: order.cancel_date ? `cancel ${formatShortDay(order.cancel_date)}` : "" },
      { text: order.wh ? `WH ${order.wh}` : "" },
      { text: deposcoStage(pts) },
    ].filter((p) => p.text);
  return [
    { text: "Found in A2000" },
    { text: order.customer_name || order.customer_code || "—" },
    { text: order.customer_po ? `PO ${order.customer_po}` : "" },
    { text: short, ...(short !== division ? { tip: division } : {}) },
    { text: order.qty !== null ? `${order.qty.toLocaleString("en-US")} units` : "" },
    { text: order.start_date ? `start ${formatShortDay(order.start_date)}` : "" },
    { text: order.cancel_date ? `cancel ${formatShortDay(order.cancel_date)}` : "" },
    { text: pts.pickTickets.length ? `${pts.pickTickets.length} pick ticket(s)` : "no pick ticket yet" },
  ].filter((p) => p.text);
}

export const foundText = (order: OrderRow, pts: OrderPts, abbrev?: DivisionNamer, source?: PreRouteSource): string =>
  foundParts(order, pts, abbrev, source)
    .map((p) => p.text)
    .join(" · ");

/** A pickup appointment's problem, or nothing: never in the past, never after the order's cancel date. */
export function checkAppointment(day: string, ctx: { today: string; cancel: string | null }): string | undefined {
  if (!day) return undefined;
  if (day < ctx.today) return `The pickup appointment ${formatDay(day)} is in the past`;
  if (ctx.cancel && day > ctx.cancel)
    return `The pickup appointment ${formatDay(day)} is after the order's cancel date ${formatDay(ctx.cancel)}`;
  return undefined;
}

/** A message the add form shows: the big red one (blocked) or the orange "Are you sure?". */
export interface PreRouteProblem {
  title: string;
  text: string;
  /** "already on the Worklist": the line to go to (a key or a stored id). */
  goTo?: string;
}

export type PreRouteCheck =
  | { ok: false; field: "orderNo" | "appointmentDate"; problem: PreRouteProblem }
  | { ok: true; found: string; foundParts: FoundPart[]; warning: PreRouteProblem | null };

/**
 * A new pre-route, checked (spec §3.6, §5): its source takes new pre-routes (`PRE_ROUTE_ADD_SOURCES`),
 * the order exists, is open, is no other pre-route (but a
 * Cancelled one), has no pick ticket yet — a Deposco SO: is not on a line yet; the appointment is
 * not in the past nor after the cancel date. On hold (A2000) is a warning. `lineOfPt` names the
 * line a pick ticket (SO) is on, for Go to line.
 */
export function checkNewPreRoute(
  input: { orderNo: string; appointmentDate: string; today: string; source?: PreRouteSource },
  ctx: {
    order: OrderRow | null;
    pts: OrderPts;
    preRoutes: readonly Pick<PreRouteRecord, "id" | "orderNo" | "status">[];
    lineOfPt?: (pt: string) => string | undefined;
    /** The division's abbreviation (`divisions.ts`); absent, its default. */
    abbrev?: DivisionNamer;
  },
): PreRouteCheck {
  const blocked = (field: "orderNo" | "appointmentDate", problem: PreRouteProblem): PreRouteCheck => ({
    ok: false,
    field,
    problem,
  });
  if (input.source && !PRE_ROUTE_ADD_SOURCES.includes(input.source))
    return blocked("orderNo", {
      title: "Pre-route is for A2000 orders",
      text: "Every open Deposco SO is on the Worklist from the moment it exists (Deposco is only the WMS: M3 has allocated it). Route it on its Worklist line.",
    });
  if (input.source === "Deposco") return checkDeposco(input, ctx, blocked);
  const no = orderNoOf(input.orderNo);
  if (!/^\d{1,10}$/.test(no))
    return blocked("orderNo", {
      title: "Not an A2000 order #",
      text: "Type the A2000 order # (CTRL_NO), 7 digits — e.g. 3865341. The PO is not the order #.",
    });
  const { order, pts } = ctx;
  if (!order)
    return blocked("orderNo", {
      title: `Order ${no} isn't in A2000`,
      text: "No open A2000 order has this order # (CTRL_NO). Check the number: a dropship order, or one closed more than 30 days ago, isn't offered.",
    });
  if (order.is_open !== 1)
    return blocked("orderNo", {
      title: `Order ${no} is not open`,
      text: `A2000 has it ${closedWords(order)}. Only an open order (OPEN or PART) can be pre-routed.`,
    });
  const other = ctx.preRoutes.find((p) => p.orderNo === no && p.status !== "Cancelled");
  if (other)
    return blocked("orderNo", {
      title: `Order ${no} is already a pre-route`,
      text: `It is pre-route ${other.id} (${other.status}). Change that one instead.`,
    });
  if (pts.pickTickets.length) {
    const first = pts.inWms[0] ?? pts.pickTickets[0]!;
    return blocked("orderNo", {
      title: `Order ${no} is already on the Worklist`,
      text: `It has ${pts.pickTickets.length === 1 ? "a pick ticket" : `${pts.pickTickets.length} pick tickets`} (${pts.pickTickets.slice(0, 3).join(", ")}${pts.pickTickets.length > 3 ? "…" : ""})${pts.inWms.length ? "" : ", not in the WMS yet"}. Route it on the Worklist line instead.`,
      goTo: ctx.lineOfPt?.(first),
    });
  }
  const appt = checkAppointment(input.appointmentDate, { today: input.today, cancel: order.cancel_date });
  if (appt) return blocked("appointmentDate", { title: "Check the pickup appointment", text: appt });
  return {
    ok: true,
    found: foundText(order, pts, ctx.abbrev),
    foundParts: foundParts(order, pts, ctx.abbrev),
    warning: order.order_hold
      ? {
          title: `Order ${no} is on hold in A2000`,
          text: `A2000 has it on hold (${order.order_hold}). Pre-route it anyway?`,
        }
      : null,
  };
}

/** `checkNewPreRoute` for a Deposco SO: the same checks, in Deposco's words. */
function checkDeposco(
  input: { orderNo: string; appointmentDate: string; today: string },
  ctx: Parameters<typeof checkNewPreRoute>[1],
  blocked: (field: "orderNo" | "appointmentDate", problem: PreRouteProblem) => PreRouteCheck,
): PreRouteCheck {
  const no = soNoOf(input.orderNo);
  if (!isSoNo(no))
    return blocked("orderNo", {
      title: "Not a Deposco SO #",
      text: "Type the Deposco SO #, e.g. SO230792. The PO is not the SO #.",
    });
  const { order, pts } = ctx;
  if (!order)
    return blocked("orderNo", {
      title: `${no} isn't in Deposco`,
      text: "No Deposco B2B order has this SO #. Check the number: an e-commerce order, or one closed more than 30 days ago, isn't offered.",
    });
  if (order.is_open !== 1)
    return blocked("orderNo", {
      title: `${no} is not open`,
      text: `Deposco has it ${order.status || "closed"}. Only an open SO can be pre-routed.`,
    });
  const other = ctx.preRoutes.find((p) => p.orderNo === no && p.status !== "Cancelled");
  if (other)
    return blocked("orderNo", {
      title: `${no} is already a pre-route`,
      text: `It is pre-route ${other.id} (${other.status}). Change that one instead.`,
    });
  if (pts.pickTickets.length)
    return blocked("orderNo", {
      title: `${no} is already on the Worklist`,
      text: LINES_WAIT_FOR_ALLOCATION
        ? `Deposco has it allocated (${order.status || "allocated"}). Route it on its Worklist line instead.`
        : `Deposco has it open (${order.status || "open"})${pts.inWms.length ? "" : ", waiting for a group"}. Route it on its Worklist line instead.`,
      goTo: pts.inWms.length ? ctx.lineOfPt?.(no) : undefined,
    });
  const appt = checkAppointment(input.appointmentDate, { today: input.today, cancel: order.cancel_date });
  if (appt) return blocked("appointmentDate", { title: "Check the pickup appointment", text: appt });
  return {
    ok: true,
    found: foundText(order, pts, ctx.abbrev, "Deposco"),
    foundParts: foundParts(order, pts, ctx.abbrev, "Deposco"),
    warning: null,
  };
}

/** The order's fields kept on the pre-route as added: shown once A2000 no longer lists the order. */
export const orderSnapshot = (o: OrderRow) => ({
  customerName: o.customer_name || o.customer_code || "",
  customerPo: o.customer_po ?? "",
  division: o.division ?? "",
  qty: o.qty,
  startDate: o.start_date,
  cancelDate: o.cancel_date,
});

/** What happens to a pre-route next: its entries move, A2000 closed it, or it waits (and why). */
export type NextStep =
  | { kind: "move" }
  | {
      kind: "close";
      status: "Cancelled" | "Shipped";
      orderStatus: string;
      canceledOn: string | null;
      cancelCode: string;
    }
  | { kind: "wait"; why: "no-pt" | "not-in-wms" }
  | { kind: "none" };

/** How long a move claimed and not finished stays its session's: then another session takes it again. */
export const MOVE_CLAIM_STALE_MS = 5 * 60_000;

export function nextStep(
  r: Pick<PreRouteRecord, "status" | "source" | "movedAt" | "moveDone">,
  order: OrderRow | null,
  pts: OrderPts,
  now = Date.now(),
): NextStep {
  // A move a session claimed and never finished (its tab closed): taken again once stale.
  if (r.status === "Moved")
    return r.moveDone !== "Yes" && now - Date.parse(r.movedAt || "1970-01-01") > MOVE_CLAIM_STALE_MS
      ? { kind: "move" }
      : { kind: "none" };
  if (r.status !== "Waiting") return { kind: "none" };
  if (pts.inWms.length) return { kind: "move" };
  if (pts.pickTickets.length) return { kind: "wait", why: "not-in-wms" };
  const closed = order && order.is_open !== 1 ? closedAs(order, r.source) : undefined;
  if (order && closed)
    return {
      kind: "close",
      status: closed,
      orderStatus: r.source === "A2000" ? String(order.status).toUpperCase() : String(order.status ?? ""),
      canceledOn: order.canceled_on,
      cancelCode: order.cancel_code ?? "",
    };
  return { kind: "wait", why: "no-pt" };
}

/** An alert: "warn" (pickup soon), "late" (the pickup passed). */
export interface PreRouteAlert {
  level: "warn" | "late";
  text: string;
}

/** Alert when the pickup is this many business days away or less (spec §3.12, Q8). */
export const ALERT_BUSINESS_DAYS = 2;

/** A waiting pre-route's alert, or null: its pickup is ≤ 2 business days away, or passed. */
export function preRouteAlert(
  appointment: string | null,
  why: "no-pt" | "not-in-wms",
  today: string,
  holidays: Holidays,
  source: PreRouteSource = "A2000",
): PreRouteAlert | null {
  if (!appointment) return null;
  const words = WAIT_WORDS[source][why];
  const still = words.still;
  if (appointment < today) return { level: "late", text: `Pickup was ${formatShortDay(appointment)} — ${words.was}` };
  const n = businessDaysBetween(today, appointment, holidays);
  if (n > ALERT_BUSINESS_DAYS) return null;
  // A pickup on a weekend or a holiday is 0 business days away, but not today.
  const when =
    appointment === today
      ? "Pickup today"
      : n === 0
        ? `Pickup ${dayOfWeek(appointment)} ${formatShortDay(appointment)}`
        : `Pickup in ${n} day${n === 1 ? "" : "s"}`;
  return { level: "warn", text: `${when} — ${still}` };
}

/** A pre-route as the page shows it: the live order when A2000 still lists it, else as added. */
export interface PreRouteView {
  record: PreRouteRecord;
  order: OrderRow | null;
  customerName: string;
  /**
   * How the pages show its customer (`customerNamer`): the order's by source + code; one gone from the
   * views by the name kept on it.
   */
  customer: CustomerFace;
  customerPo: string;
  division: string;
  /** The division as the pages show it (`divisions.ts`); `division` is its full name. */
  divisionAbbrev: string;
  dc: string;
  /**
   * Moved: its pick tickets as its lines keep them; else the open pick tickets' QTY once there are
   * any, else the order's open qty (`qtyFrom`).
   */
  qty: number | null;
  /** Where Qty comes from: the moved lines' copies, the open pick tickets, or the order while it has none. */
  qtyFrom: "copies" | "pickTickets" | "order";
  /** How a row's Qty and Order value are got, on hover. */
  qtyTip: string;
  valueTip: string;
  start: string | null;
  cancel: string | null;
  /** From the pick tickets once they exist; "" until then (most open orders have no WMS). */
  wh: string;
  next: NextStep;
  /** Where it stands, in words: "No pick ticket yet", "Waiting to move — opens on the next refresh". */
  stageText: string;
  alert: PreRouteAlert | null;
  /** The red note of a pre-route A2000 closed: "Cancelled in A2000 (CNCL) on 10/02 — no pick ticket was made". */
  closedNote: string;
  movedLineIds: string[];
  /**
   * Managers only: the open pick tickets' $ value once the order has any, else the order's open
   * $ value — but null for a Moved one whose pick tickets all left the open view (never stored).
   */
  value: number | null;
}

export function preRouteView(
  r: PreRouteRecord,
  ctx: {
    order: OrderRow | null;
    pts: OrderPts;
    today: string;
    holidays: Holidays;
    /** The order's open $ value. */
    value?: number | null;
    /** Its open pick tickets' $ value: what a manager sees once there are any. */
    ptValue?: number | null;
    /** A Moved one's pick tickets as its lines keep them (`movedCopies`). */
    copies?: MovedCopies | null;
    abbrev?: DivisionNamer;
    customer?: CustomerNamer;
  },
): PreRouteView {
  const o = ctx.order;
  const fromPts = ctx.pts.pickTickets.length > 0;
  const moved = r.status === "Moved";
  const qtyFrom = moved && ctx.copies ? "copies" : fromPts ? "pickTickets" : "order";
  const deposco = r.source === "Deposco";
  const openTip = deposco
    ? `The SO's own qty and value${LINES_WAIT_FOR_ALLOCATION ? " (allocated)" : ""}`
    : "From the order's open pick tickets";
  const orderTip = deposco
    ? `The SO's qty and value (${DEPOSCO_WAITS_FOR})`
    : "The order's open qty and value (no pick ticket yet)";
  const next = nextStep(r, o, ctx.pts);
  const stageText =
    r.status === "Moved"
      ? r.moveDone === "Yes"
        ? "Moved to the Worklist"
        : "Moving to the Worklist…"
      : r.status === "Cancelled" || r.status === "Shipped"
        ? `${r.status} in ${r.source}`
        : next.kind === "move"
          ? "Waiting to move — opens on the next refresh"
          : next.kind === "close"
            ? `${next.status} in ${r.source} — updates on the next refresh`
            : WAIT_WORDS[r.source][next.kind === "wait" ? next.why : "no-pt"].now;
  const closedNote = deposco
    ? r.status === "Cancelled"
      ? `Canceled in Deposco${r.orderStatus ? ` (${r.orderStatus})` : ""} — the pre-route never moved`
      : r.status === "Shipped"
        ? `Shipped in Deposco (${r.orderStatus || DEPOSCO_SHIPPED}) — the pre-route never moved`
        : ""
    : r.status === "Cancelled"
      ? `Cancelled in A2000${r.orderStatus ? ` (${r.orderStatus}${r.cancelCode ? `, code ${r.cancelCode}` : ""})` : ""}${r.canceledOn ? ` on ${formatDay(r.canceledOn)}` : ""} — no pick ticket was made`
      : r.status === "Shipped"
        ? "Shipped in A2000 (SHIP) — never had a pick ticket on the Worklist"
        : "";
  return {
    record: r,
    order: o,
    customerName: o ? o.customer_name || o.customer_code || "" : r.customerName,
    customer: (ctx.customer ?? customerNamer())(
      o
        ? { code: o.customer_code ?? "", name: o.customer_name ?? "", source: r.source }
        : { code: "", name: r.customerName },
    ),
    customerPo: o ? (o.customer_po ?? "") : r.customerPo,
    division: o ? (o.division ?? "") : r.division,
    divisionAbbrev: (ctx.abbrev ?? divisionNamer())(
      o ? { corp: o.corp ?? "", div: o.div ?? "", name: o.division ?? "" } : { name: r.division },
    ),
    dc: o ? o.dc_name || o.dc_no || "" : "",
    qty: qtyFrom === "copies" ? ctx.copies!.qty : fromPts ? ctx.pts.qty : o ? o.qty : r.qty,
    qtyFrom,
    qtyTip:
      qtyFrom === "copies"
        ? `From the ${ctx.copies!.pickTickets} pick ticket${ctx.copies!.pickTickets === 1 ? "" : "s"} on its moved lines`
        : fromPts
          ? openTip
          : orderTip,
    valueTip: fromPts ? openTip : moved ? MOVED_NO_VALUE : orderTip,
    start: o ? o.start_date : r.startDate,
    cancel: o ? o.cancel_date : r.cancelDate,
    wh: ctx.pts.whs.join(", "),
    next,
    stageText,
    alert:
      r.status === "Waiting" && next.kind === "wait"
        ? preRouteAlert(r.appointmentDate, next.why, ctx.today, ctx.holidays, r.source)
        : null,
    closedNote,
    movedLineIds: r.movedLineIds ? r.movedLineIds.split(",").filter(Boolean) : [],
    value: (fromPts ? ctx.ptValue : moved ? null : ctx.value) ?? null,
  };
}

/** A Moved pre-route's Order value once its pick tickets left the open view: none is shown, none stored. */
export const MOVED_NO_VALUE = "Pick tickets shipped — no open value";

/** The page's sub-tabs (Figure 30): Waiting · Alerts · Moved to worklist · All. */
export type PreRouteTab = "waiting" | "alerts" | "moved" | "all";
export const PRE_ROUTE_TABS: readonly { id: PreRouteTab; label: string; hint?: string }[] = [
  // Both sources wait: A2000 for its pick ticket, Deposco (pre-routes from before) for its line.
  { id: "waiting", label: "Waiting", hint: `A2000: no pick ticket in the WMS yet · Deposco: ${DEPOSCO_WAITS_FOR}` },
  { id: "alerts", label: "Alerts" },
  { id: "moved", label: "Moved to worklist" },
  { id: "all", label: "All" },
];

export function inPreRouteTab(v: Pick<PreRouteView, "record" | "alert">, tab: PreRouteTab): boolean {
  if (tab === "waiting") return v.record.status === "Waiting";
  if (tab === "alerts") return !!v.alert;
  if (tab === "moved") return v.record.status === "Moved";
  return true;
}

/** The red badge on the Pre-route tab: its alerts. */
export const preRouteAlertCount = (views: readonly Pick<PreRouteView, "alert">[]) =>
  views.filter((v) => v.alert).length;

/** A pre-route's search text: order #, customer, PO, division (name or abbreviation), routing ref (Figure 30's search). */
export const preRouteMatches = (v: PreRouteView, q: string) =>
  !q.trim() ||
  [
    v.record.orderNo,
    v.customer.text,
    v.customerName,
    v.order?.customer_code ?? "",
    v.customerPo,
    v.division,
    v.divisionAbbrev,
    v.record.routingRef,
    v.record.notes,
  ]
    .join(" ")
    .toLowerCase()
    .includes(q.trim().toLowerCase());

/** How the System log says a move made in someone's session: never as if that person typed it. */
export const moveNote = (person: string) => `Pre-route (automatic) — applied in ${person || "someone"}'s session`;

/** A value a line already had and kept: "Routing Reference “RTS# 1” (pre-route: “RTS# 2”)". */
export interface KeptValue {
  field: WarehouseField;
  label: string;
  line: string;
  preRoute: string;
}

export const keptText = (k: KeptValue) => `${k.label} “${k.line}” (pre-route: “${k.preRoute}”)`;

const LABELS: Partial<Record<WarehouseField, string>> = {
  routingRef: "Routing Reference",
  routingStatus: "Routing status",
  appointmentDate: "Appointment date",
};

/** One line a move writes: what changes on it, old → new, and what it kept. */
export interface LineMove {
  line: LineView;
  change: Partial<RoutingLineRecord>;
  changes: FieldChange[];
  kept: KeptValue[];
}

/** Routing Notes are NVARCHAR(500). */
const NOTES_MAX = 500;

/**
 * What a pre-route writes on the open Worklist lines holding its order's pick tickets in the WMS:
 * Routing Reference, Routing status "Pre-routed" (with Routed on today, `editSideEffects`'s rule),
 * the appointment date — each only where the line has none — and "Pre-route: <notes>" added to the
 * Routing Notes unless they hold it. Writing the same plan twice writes nothing the second time.
 */
export function movePlan(
  r: Pick<PreRouteRecord, "orderNo" | "routingRef" | "appointmentDate" | "notes"> &
    Partial<Pick<PreRouteRecord, "source">>,
  lines: readonly LineView[],
  today: string,
): LineMove[] {
  // A Deposco SO is the line's own pick ticket; an A2000 order's pick tickets name it by CTRL_NO.
  const ours = (p: LineView["pts"][number]) =>
    r.source === "Deposco"
      ? !!p.source?.deposco && p.pickTicket === r.orderNo
      : !p.source?.deposco && p.source?.ctrl_no === r.orderNo && p.source.in_wms === 1;
  const targets = lines.filter((l) => !l.finished && !l.noWms && l.pts.some(ours));
  return targets.map((line) => {
    const change: Partial<RoutingLineRecord> = {};
    const kept: KeptValue[] = [];
    const fill = (field: "routingRef" | "routingStatus" | "appointmentDate", value: string) => {
      if (!value) return;
      const now = line.fields[field];
      if (isBlankEntry(field, now)) (change as Record<string, string>)[field] = value;
      else if (now !== value) kept.push({ field, label: LABELS[field]!, line: now, preRoute: value });
    };
    fill("routingRef", r.routingRef.trim());
    fill("routingStatus", "Pre-routed");
    if (change.routingStatus) Object.assign(change, editSideEffects(line, "routingStatus", "Pre-routed", "", today));
    fill("appointmentDate", r.appointmentDate ?? "");
    const note = r.notes.trim() ? `Pre-route: ${r.notes.trim()}` : "";
    const notes = line.fields.routingNotes;
    if (note && !notes.includes(note)) change.routingNotes = (notes ? `${notes} · ${note}` : note).slice(0, NOTES_MAX);
    const changes = Object.entries(change).flatMap(([k, v]) =>
      fieldChange(k, (line.fields as unknown as Record<string, unknown>)[k] ?? "", v),
    );
    return { line, change, changes, kept };
  });
}

/** The pre-route each line came from (Moved, its lines named): the Worklist's "Pre-routed" marker. */
export function preRouteOfLine(preRoutes: readonly PreRouteRecord[]): Map<string, PreRouteRecord> {
  const out = new Map<string, PreRouteRecord>();
  for (const p of preRoutes)
    if (p.status === "Moved") for (const id of p.movedLineIds.split(",").filter(Boolean)) out.set(id, p);
  return out;
}

/** The marker's tooltip: "From pre-route 3865341, added by Isaac Shalita on 10/20/26". */
export const preRouteMarkerText = (p: PreRouteRecord) =>
  `From pre-route ${p.orderNo}${p.addedBy ? `, added by ${p.addedBy}` : ""}${p.created_at ? ` on ${formatDay(p.created_at.slice(0, 10))}` : ""}`;

/** `GET /api/routing/pre-routes`: every pre-route, its order and pick tickets, and the lines a move writes. */
export interface PreRouteData {
  preRoutes: PreRouteRecord[];
  /** The pre-routes' orders A2000 still lists (`routing-orders`), by order #. */
  orders: Record<string, OrderRow>;
  /** Their open pick tickets, by order #; an order with none is absent. */
  orderPts: Record<string, OrderPts>;
  /** Managers only, else null: order # → open $ value (`routing-order-values`). */
  orderValues: Record<string, number> | null;
  /** Managers only, else null: order # → its open pick tickets' $ value (`orderPtValues`). */
  ptValues: Record<string, number> | null;
  /** Moved pre-routes' pick tickets as their lines keep them, by pre-route id (`movedCopies`): no $ value. */
  movedCopies: Record<string, MovedCopies>;
}

/** How the add form found an order: by its own # (CTRL_NO, SO #) or by its customer PO. */
export type PreRouteMatchBy = "order" | "po";

/** One order as the add form checks it. */
export interface PreRouteOrderLookup {
  orderNo: string;
  order: OrderRow | null;
  pts: OrderPts;
  /** The line a pick ticket of the order is on: its stored id, else its key. */
  lines: Record<string, string>;
  /** The pre-routes of this order # now: the duplicate check. */
  existing: PreRouteRecord[];
  by: PreRouteMatchBy;
  /** A Deposco SO's group, shown in the list of a PO's orders; null for A2000. */
  group: string | null;
}

/**
 * `GET /api/routing/pre-routes/order?no=&source=`: the orders what was typed names — one by its
 * order # (CTRL_NO, SO #), or every order of a PO. A PO is never stored as a pre-route's order #.
 */
export interface PreRouteOrderMatches {
  typed: string;
  /** "po" when any order was found by its PO: a 7-digit number can be a CTRL_NO and a PO at once. */
  by: PreRouteMatchBy;
  /** Its own # first, then by order #; at most `PRE_ROUTE_MATCHES_MAX`. */
  matches: PreRouteOrderLookup[];
  /** More orders have that PO than are listed. */
  truncated: boolean;
  /** The open orders found in all, listed or not: a cut list says it, so it never hides one. */
  openTotal: number;
}

/** A PO's orders listed at most; A2000's PO query reads at most `A2000_PO_ROWS_MAX`. */
export const PRE_ROUTE_MATCHES_MAX = 200;
export const A2000_PO_ROWS_MAX = 50;

/**
 * What a typed value is looked up as. Deposco: an SO # when it is one (else, or when no SO has it,
 * a PO). A2000: a CTRL_NO when it is 7–10 digits, and a PO always — a number can be both.
 */
export function preRouteLookupKeys(typed: string, source: PreRouteSource): { orderNo: string | null; po: string } {
  const po = typed.trim().slice(0, 40);
  if (source === "Deposco") {
    const so = soNoOf(typed);
    return { orderNo: isSoNo(so) ? so : null, po };
  }
  const no = orderNoOf(typed);
  return { orderNo: /^\d{7,10}$/.test(no) ? no : null, po };
}

/** A PO's orders in order: open first, then by order # — a cut only ever drops closed ones. */
export function openFirst(a: Pick<OrderRow, "is_open" | "ctrl_no">, b: Pick<OrderRow, "is_open" | "ctrl_no">): number {
  return b.is_open - a.is_open || a.ctrl_no.localeCompare(b.ctrl_no, "en", { numeric: true });
}

/**
 * A2000's PO query's rows (it reads `A2000_PO_ROWS_MAX` + 1, open first as `openFirst`): at most
 * the cap, `more` when the PO had more — the list then says it was cut off.
 */
export function capPoRows<T extends Pick<OrderRow, "is_open" | "ctrl_no">>(
  rows: readonly T[],
): { orders: T[]; more: boolean } {
  return { orders: [...rows].sort(openFirst).slice(0, A2000_PO_ROWS_MAX), more: rows.length > A2000_PO_ROWS_MAX };
}

/** The Deposco SOs of a PO, from the rows already read: exact, trimmed, never a part of one. */
export function deposcoByPo(rows: readonly DeposcoRow[], po: string): DeposcoRow[] {
  const want = po.trim();
  return want ? rows.filter((r) => (r.customer_po ?? "").trim() === want) : [];
}

/**
 * The orders found, each once: its own # first, then the PO's open first, by order # (`openFirst`);
 * at most `PRE_ROUTE_MATCHES_MAX`. `openTotal`: A2000's count when its query was cut, else counted here.
 */
export function matchedOrders(found: {
  byNo: readonly OrderRow[];
  byPo: readonly OrderRow[];
  more?: boolean;
  openTotal?: number;
}): {
  orders: { order: OrderRow; by: PreRouteMatchBy }[];
  by: PreRouteMatchBy;
  truncated: boolean;
  openTotal: number;
} {
  const own = new Set(found.byNo.map((o) => o.ctrl_no));
  const byPo = found.byPo.filter((o) => !own.has(o.ctrl_no)).sort(openFirst);
  const all = [
    ...found.byNo.map((order) => ({ order, by: "order" as const })),
    ...byPo.map((order) => ({ order, by: "po" as const })),
  ];
  return {
    orders: all.slice(0, PRE_ROUTE_MATCHES_MAX),
    by: byPo.length ? "po" : "order",
    truncated: !!found.more || all.length > PRE_ROUTE_MATCHES_MAX,
    openTotal:
      found.byNo.filter((o) => o.is_open === 1).length +
      (found.openTotal ?? byPo.filter((o) => o.is_open === 1).length),
  };
}

/** One order of a lookup in the add form: its check, whether it can be ticked, where it stands. */
export interface PreRouteChoice {
  lookup: PreRouteOrderLookup;
  check: PreRouteCheck;
  /** A pre-route is valid for it today: ticked by default. The appointment is checked at Add. */
  selectable: boolean;
  /** Why it can't be ticked: already pre-routed, already on the Worklist, closed. */
  reason: string;
  /** "no pick ticket yet", "pick ticket in WMS", a Deposco SO's "on the Worklist"… */
  stage: string;
  /** A pre-route (not Cancelled) has it: the page's, or one the lookup read. */
  preRouted: boolean;
}

/** Each order a lookup found, checked as `checkNewPreRoute` checks one. */
export function preRouteChoices(
  found: PreRouteOrderMatches,
  input: { appointmentDate: string; today: string; source: PreRouteSource },
  ctx: { preRoutes: readonly Pick<PreRouteRecord, "id" | "orderNo" | "status">[]; abbrev?: DivisionNamer },
): PreRouteChoice[] {
  const existing = found.matches.flatMap((m) => m.existing);
  const preRouted = new Set(
    [...ctx.preRoutes, ...existing].flatMap((p) => (p.status !== "Cancelled" ? [p.orderNo] : [])),
  );
  return found.matches.map((lookup) => {
    const check = checkNewPreRoute(
      { orderNo: lookup.orderNo, appointmentDate: input.appointmentDate, today: input.today, source: input.source },
      {
        order: lookup.order,
        pts: lookup.pts,
        preRoutes: [...ctx.preRoutes, ...existing],
        lineOfPt: (pt) => lookup.lines[pt],
        abbrev: ctx.abbrev,
      },
    );
    const selectable = check.ok || check.field === "appointmentDate";
    return {
      lookup,
      check,
      selectable,
      reason: !check.ok && !selectable ? check.problem.title : "",
      stage: matchStage(lookup, input.source),
      preRouted: preRouted.has(lookup.orderNo),
    };
  });
}

/** The orders ticked when a lookup's answer first shows: those a pre-route is valid for. */
export function defaultTicks(choices: readonly PreRouteChoice[]): Set<string> {
  return new Set(choices.flatMap((c) => (c.selectable ? [c.lookup.orderNo] : [])));
}

/**
 * The ticks after Add: only the orders that failed and can still be ticked — never the defaults
 * again; the ones left unticked stay so.
 */
export function ticksAfterAdd(choices: readonly PreRouteChoice[], failed: Iterable<string>): Set<string> {
  const want = new Set(failed);
  return new Set(choices.flatMap((c) => (c.selectable && want.has(c.lookup.orderNo) ? [c.lookup.orderNo] : [])));
}

/** Where an order stands, in a few words, for the list of a PO's orders. */
function matchStage(m: Pick<PreRouteOrderLookup, "order" | "pts">, source: PreRouteSource): string {
  if (m.order && m.order.is_open !== 1)
    return source === "A2000" ? closedWords(m.order) : `closed (${m.order.status || "no status"})`;
  if (source === "Deposco") return deposcoStage(m.pts);
  if (m.pts.inWms.length) return "pick ticket in WMS";
  return m.pts.pickTickets.length ? "pick ticket, not in WMS yet" : "no pick ticket yet";
}

/** Every pre-route as the page shows it, newest first: its order, pick tickets, alert and (managers) $ value. */
export function preRouteViews(
  data: PreRouteData & {
    divisionAbbrevs?: readonly DivisionAbbrevRecord[];
    customerNames?: readonly CustomerNameRecord[];
  },
  today: string,
  holidays: Holidays,
): PreRouteView[] {
  const abbrev = divisionNamer(data.divisionAbbrevs);
  const customer = customerNamer(data.customerNames);
  return data.preRoutes
    .map((r) =>
      preRouteView(r, {
        order: data.orders[r.orderNo] ?? null,
        pts: data.orderPts[r.orderNo] ?? NO_PTS,
        today,
        holidays,
        value: data.orderValues?.[r.orderNo] ?? null,
        ptValue: data.ptValues?.[r.orderNo] ?? null,
        copies: data.movedCopies[r.id] ?? null,
        abbrev,
        customer,
      }),
    )
    .sort((a, b) => String(b.record.created_at ?? "").localeCompare(String(a.record.created_at ?? "")));
}

/** Ask AI's question of the pre-routes (`POST /api/routing/ask/pre-routes`): read only. */
export interface PreRouteQuery {
  tab?: PreRouteTab;
  /** A2000 or Deposco pre-routes only; absent, both. */
  source?: PreRouteSource;
  /** Customer names or parts of them. */
  customer?: string[];
  /** An order #, PO, customer or routing ref. */
  search?: string;
  /** The page's rows (its view): the ids it shows; absent, every pre-route. */
  within?: string[];
  /** Rows to list, 0–25 (default 10). Counts cover every match. */
  limit?: number;
}

/** One pre-route as Ask AI reads it: short, no $ value unless the reader is a manager. */
export interface PreRouteAnswerRow {
  source: PreRouteSource;
  /** The A2000 order # (CTRL_NO), or the Deposco SO #. */
  orderNo: string;
  status: PreRouteStatus;
  /** As the pages show it (Admin → Customers); `customer_name` the original. */
  customer: string;
  customer_name: string;
  po: string;
  /** The abbreviation, then the full name when it differs: "HANDBAG (HANDBAGS)". */
  division: string;
  appointment: string | null;
  routingRef: string;
  stage: string;
  alert: string | null;
  movedLines: number;
  value?: number | null;
}

/** Counts by tab and alert, and the first rows: under ~3 KB (the chat's body limit). */
export function askPreRoutes(
  views: readonly PreRouteView[],
  q: PreRouteQuery,
  manager: boolean,
): {
  matching: number;
  byTab: Record<PreRouteTab, number>;
  alerts: { soon: number; late: number };
  rows: PreRouteAnswerRow[];
} {
  const within = q.within ? new Set(q.within) : null;
  const names = (q.customer ?? []).map((c) => c.toLowerCase()).filter(Boolean);
  const base = views.filter(
    (v) =>
      (!within || within.has(v.record.id)) &&
      (!q.source || v.record.source === q.source) &&
      // The shown name, the original or the code (the hover has both).
      (!names.length ||
        names.some((n) => `${v.customer.text} ${v.customerName} ${v.customer.tip}`.toLowerCase().includes(n))) &&
      preRouteMatches(v, q.search ?? ""),
  );
  const matched = base.filter((v) => inPreRouteTab(v, q.tab ?? "all"));
  const limit = Math.max(0, Math.min(25, q.limit ?? 10));
  const byTab = Object.fromEntries(
    PRE_ROUTE_TABS.map((t) => [t.id, base.filter((v) => inPreRouteTab(v, t.id)).length]),
  ) as Record<PreRouteTab, number>;
  return {
    matching: matched.length,
    byTab,
    alerts: {
      soon: base.filter((v) => v.alert?.level === "warn").length,
      late: base.filter((v) => v.alert?.level === "late").length,
    },
    rows: [...matched]
      .sort((a, b) => (a.record.appointmentDate ?? "9999").localeCompare(b.record.appointmentDate ?? "9999"))
      .slice(0, limit)
      .map((v) => ({
        source: v.record.source,
        orderNo: v.record.orderNo,
        status: v.record.status,
        customer: v.customer.text,
        customer_name: v.customerName,
        po: v.customerPo.slice(0, 40),
        division: divisionWords(v.divisionAbbrev, v.division).slice(0, 60),
        appointment: v.record.appointmentDate,
        routingRef: v.record.routingRef.slice(0, 40),
        stage: v.closedNote || v.stageText,
        alert: v.alert?.text ?? null,
        movedLines: v.movedLineIds.length,
        ...(manager ? { value: v.value } : {}),
      })),
  };
}
